import type { WorkflowDefinition } from "../types";

/**
 * W29 — Map a category in AI answers.
 *
 * Organization-scoped for the same reason W14 and W19 are: the subject does not
 * exist yet, so step one creates the CATEGORY property and the run binds to it.
 * `perTarget` on that property, so an agency mapping three markets in the same
 * week gets three checklists rather than three people sharing one.
 *
 * The order follows W2's, with one market-shaped difference: the brands come
 * *out* of the first report rather than being named up front. The report also
 * writes the market its first prompt set when there is none, so nothing is
 * blocked behind the Prompts page. The sequence is name it, report, review the
 * brands the report found, round the prompt set out, then track daily.
 */
export const categoryMapWorkflow: WorkflowDefinition = {
	id: "category-map",
	wNumber: 29,
	title: "Map a category in AI answers",
	outcome:
		"See every brand AI names when buyers ask about a market, and watch the ranking move every day.",
	audiences: ["pr", "agencies", "inhouse"],
	uses: ["Properties", "Prompts", "AI Reports", "Brands", "Projects"],
	scope: "organization",
	bindsProperty: true,
	bindSubjectType: "CATEGORY",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "property", label: "Category" },
	// Any organization can map a market, and the subject this playbook creates
	// is a CATEGORY by construction, so there is nothing here to gate on.
	isApplicable: () => true,
	articleSlug: "how-to-map-a-category-in-ai-answers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "create-category",
			title: "Name the category",
			page: "Organization dashboard",
			route: "org:/",
			criterion: "You've named the category.",
			why: "A category has no brand of its own. It is the market, and the brands AI names inside it are what you measure.",
			docsHref: "/docs/getting-started/overview",
			tasks: [
				"Add a property and pick Category as the subject",
				"Name the market the way buyers say it",
				"Add the other phrases buyers use for the same market",
			],
			// Completion is this playbook reaching a category of its own, not
			// the organization having categories in it: the third market is not
			// finished because the first two exist.
			complete: { kind: "bound", subjectType: "CATEGORY" },
			signal: ({ run }) => ({
				kind: "checklist",
				items: [
					{
						label: "Category named",
						done: Boolean(run?.propertyId),
					},
				],
			}),
		},
		{
			id: "run-report",
			title: "Start the report",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've completed one report.",
			why: "The report is what makes the map. It writes the market a first set of prompts, asks them across the AI platforms, and comes back with the brands.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Start a report and wait 4 to 15 minutes",
				"Read which brands AI names most often",
				"Note the brands you did not expect to see",
			],
			complete: { kind: "count", key: "completedReportCount", gte: 1 },
			spend: { kind: "aiReport" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Completed reports",
							value: state.property.completedReportCount,
							target: 1,
						}
					: null,
		},
		{
			id: "review-brands",
			title: "Review the brands AI named",
			page: "Brands",
			route: "/competitors",
			criterion: "You've kept at least five brands.",
			why: "The report suggests the brands it found in the answers. Keeping the right ones is what makes share of voice mean something.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Remove anything that is not a real brand in this market",
				"Add the brands buyers ask about that AI missed",
				"Add short names, nicknames and former names",
			],
			complete: { kind: "count", key: "competitorCount", gte: 5 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Brands tracked",
							value: state.property.competitorCount,
							target: 5,
						}
					: null,
		},
		{
			id: "generate-prompts",
			title: "Round out the prompts buyers ask",
			page: "Prompts",
			route: "/discovery-queries",
			criterion:
				"You've added 15 or more prompts across two buying stages.",
			why: "The report leaves you a starter set. Fifteen or more, across two buying stages, is what a ranking you can trust is built on.",
			docsHref: "/docs/getting-started/choosing-prompts",
			tasks: [
				"Generate more prompts from the frameworks that fit the market",
				"Cover the problem, the comparison and the shortlist",
				"Tag the set so every dashboard can filter to it",
			],
			// Proxy for the coverage score, as in W2: computing the real one
			// embeds every prompt, which has no business in a loader. The step
			// panel hydrates the true score lazily.
			complete: {
				kind: "all",
				of: [
					{ kind: "count", key: "activePromptCount", gte: 15 },
					{ kind: "count", key: "promptStageCount", gte: 2 },
				],
			},
			enrichment: "coverageScore",
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active prompts",
							value: state.property.activePromptCount,
							target: 15,
							caption: `${state.property.promptStageCount} of 3 buying stages covered`,
						}
					: null,
		},
		{
			id: "track-daily",
			title: "Track it every day",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set up one active project.",
			why: "A single report is one day of a market. Daily tracking shows which brand is gaining and which one is fading.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a General project named for the market",
				"Add the category prompts to it",
				"Set the start date and any goals",
			],
			complete: { kind: "count", key: "activeProjectCount", gte: 1 },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Active project",
									done: state.property.activeProjectCount > 0,
								},
								{
									label: "Prompts assigned",
									done: state.property.activePromptCount > 0,
								},
							],
						}
					: null,
		},
	],
};
